import { useEffect, useRef, useState } from "react";

const isDocumentVisible = (): boolean =>
  typeof document === "undefined" || document.visibilityState === "visible";

/**
 * PRODUCT_DEV-652: タブが表示中かどうかを返す。
 *
 * ポーリングを裏タブで止めるための共通判定。`visibilitychange` に加えて `pageshow` も見るのは、
 * bfcache 復帰では `visibilitychange` が発火しない環境があるため
 * （`packages/shared-ui/hooks/useScreenWakeLock.ts` と同じ理由・同じ扱い）。
 */
export function usePageVisible(): boolean {
  const [visible, setVisible] = useState(isDocumentVisible);

  useEffect(() => {
    const sync = () => setVisible(isDocumentVisible());
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("pageshow", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("pageshow", sync);
    };
  }, []);

  return visible;
}

/**
 * PRODUCT_DEV-951: 裏に回った画面が**表に戻った回数**を返す（最初の表示は 0）。
 *
 * 開きっぱなしの画面が、戻ったときに取り直すための合図。effect の依存に入れると
 * 戻るたびに1回だけ走る。手帳は開いたときに1回しか取りに行かないため、夕方に作られる
 * 週報の下書きや、管理者が変えた設定が、開き直すまで画面に出てこなかった。
 */
export function usePageReturnCount(): number {
  const visible = usePageVisible();
  const wasHidden = useRef(!visible);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!visible) {
      wasHidden.current = true;
      return;
    }
    if (wasHidden.current) {
      wasHidden.current = false;
      setCount((c) => c + 1);
    }
  }, [visible]);

  return count;
}
